import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

export default function AsignarCobradorDialog({ open, onClose, onConfirm, rutaId }) {
  const [cobradores, setCobradores] = useState([]);
  const [selectedCobrador, setSelectedCobrador] = useState('');
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  useEffect(() => {
    if (open) {
      fetchCobradores();
    }
  }, [open]);

  const fetchCobradores = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cobradores/disponibles/${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCobradores(data);
      } else {
        setMessageAlert('Error al cargar los cobradores disponibles');
        setOpenAlert(true);
      }
    } catch (error) {
      console.error('Error fetching cobradores:', error);
      setMessageAlert('Error al cargar los cobradores disponibles');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCobrador) {
      setMessageAlert('Selecciona un cobrador para asignar');
      setOpenAlert(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/rutas/${rutaId}/asignar-cobrador`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          id_cobrador: selectedCobrador
        })
      });

      if (response.ok) {
        setMessageAlert('Cobrador asignado exitosamente');
        setOpenAlert(true);
        onConfirm();
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al asignar cobrador');
      }
    } catch (error) {
      setMessageAlert(error.message || 'Error al asignar cobrador');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCobrador('');
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              <PersonAddIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Asignar Cobrador a Ruta
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item size={12}>
              <Typography variant="body1" fontWeight={600} mb={2}>
                Seleccionar Cobrador
              </Typography>
              <FormControl fullWidth>
                <Select
                  sx={{
                    backgroundColor: 'white'
                  }}
                  placeholder="Seleccionar cobrador"
                  value={selectedCobrador}
                  onChange={(e) => setSelectedCobrador(e.target.value)}
                  disabled={loading}
                >
                  {cobradores.map((cobrador) => (
                    <MenuItem key={cobrador.id} value={cobrador.id}>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {cobrador.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cobrador.identificacion} - {cobrador.telefono}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {cobradores.length === 0 && !loading && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  No hay cobradores activos disponibles. Crea un cobrador primero.
                </Alert>
              </Grid>
            )}

            {loading && (
              <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Cargando cobradores...
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !selectedCobrador}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            color="primarybutton"
            sx={{ 
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Asignando...' : 'Asignar Cobrador'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
}
